import {
  EBT_KEYS,
  FLEET_OVERRIDE_FIELDS,
  formatFee,
  formatFnsNumber,
  formatPercent,
  PIN_DEBIT_KEYS,
  VOYAGER_KEYS,
  WEX_KEYS,
  type FleetOverrideField,
  type PricingTier,
} from "@/lib/pricingTiers";
import type { FleetOverridesForm } from "./types";
import { TF } from "./TF";

export function FleetPricingAdjustmentSection({
  idPrefix,
  tier,
  fleetOverrides,
  onFleetOverrideChange,
  errorAt,
}: {
  idPrefix: string;
  tier: PricingTier | null;
  fleetOverrides: FleetOverridesForm;
  onFleetOverrideChange: (key: keyof FleetOverridesForm, v: string) => void;
  errorAt?: (field: string) => string | undefined;
}) {
  function renderField(field: FleetOverrideField) {
    const id = `${idPrefix}.${field.key}`;
    const key = field.key as keyof FleetOverridesForm;
    const value = fleetOverrides[key];
    const tierValue = tier?.fleetPricing[field.key];
    const placeholder =
      tierValue == null
        ? "Tier default"
        : field.kind === "percent"
          ? formatPercent(tierValue)
          : field.kind === "number"
            ? field.key === "ebtFnsNumber"
              ? formatFnsNumber(tierValue)
              : String(tierValue)
            : formatFee(tierValue);
    return (
      <TF
        key={field.key}
        id={id}
        label={field.label}
        inputMode={field.kind === "number" ? "numeric" : "decimal"}
        numeric={
          field.kind === "percent"
            ? "percent"
            : field.kind === "number"
              ? "integer"
              : "currency"
        }
        value={value}
        onChange={(v) => onFleetOverrideChange(key, v)}
        maxLength={12}
        placeholder={placeholder}
        error={errorAt?.(id)}
      />
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-dashed border-brand-border/70 bg-brand-surface/30 p-3">
      <div>
        <p className="text-sm font-medium text-brand-text">
          Entitlements adjustment{" "}
          <span className="font-normal text-brand-muted">(admin only)</span>
        </p>
        <p className="mt-0.5 text-xs text-brand-muted">
          Overrides for the fleet-card schedule (Voyager, WEX, PIN debit, EBT).
          Leave any field blank to keep the tier default. Saved for MAR and
          display; never sent to CardConnect. The effective values ARE shown to
          the merchant in their pricing summary.
        </p>
      </div>
      <div>
        <p className="text-xs font-medium text-brand-text">Voyager</p>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FLEET_OVERRIDE_FIELDS.filter((f) =>
            VOYAGER_KEYS.includes(f.key),
          ).map(renderField)}
        </div>
      </div>
      <div className="border-t border-brand-border/70 pt-3">
        <p className="text-xs font-medium text-brand-text">WEX</p>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FLEET_OVERRIDE_FIELDS.filter((f) => WEX_KEYS.includes(f.key)).map(
            renderField,
          )}
        </div>
      </div>
      <div className="border-t border-brand-border/70 pt-3">
        <p className="text-xs font-medium text-brand-text">PIN debit</p>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FLEET_OVERRIDE_FIELDS.filter((f) =>
            PIN_DEBIT_KEYS.includes(f.key),
          ).map(renderField)}
        </div>
      </div>
      <div className="border-t border-brand-border/70 pt-3">
        <p className="text-xs font-medium text-brand-text">EBT</p>
        <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FLEET_OVERRIDE_FIELDS.filter((f) => EBT_KEYS.includes(f.key)).map(
            renderField,
          )}
        </div>
      </div>
    </div>
  );
}
